/**
 * IK chain state — shared data structure and capture utility.
 *
 * Used by all IK solvers (analytical, Jacobian DLS, whole-body) and the
 * pose pipeline to represent a bone chain's kinematic state at solve time.
 */

import * as THREE from "three/webgpu";

// Scratch objects — reused across calls to avoid per-frame allocations
const _v1 = new THREE.Vector3();

export interface IKChainState {
  /** World positions of each joint (length = chain bones + 1 for the tip) */
  positions: THREE.Vector3[];
  /** Bone lengths between consecutive joints */
  lengths: number[];
  /** Bone names in order */
  boneNames: string[];
  /** Local quaternions for each bone at the moment the chain was captured */
  startLocalQuats: THREE.Quaternion[];
  /** Parent world quaternions at solve start (for local conversion) */
  parentWorldQuats: THREE.Quaternion[];
}

/**
 * Capture the current state of an IK chain from live bone objects.
 */
export function captureChainState(
  bones: Map<string, THREE.Bone>,
  chainBoneNames: string[],
): IKChainState | null {
  const positions: THREE.Vector3[] = [];
  const lengths: number[] = [];
  const startLocalQuats: THREE.Quaternion[] = [];
  const parentWorldQuats: THREE.Quaternion[] = [];

  for (let i = 0; i < chainBoneNames.length; i++) {
    const bone = bones.get(chainBoneNames[i]);
    if (!bone) return null;

    const pos = new THREE.Vector3();
    bone.getWorldPosition(pos);
    positions.push(pos);

    startLocalQuats.push(bone.quaternion.clone());

    const parentQ = new THREE.Quaternion();
    if (bone.parent) bone.parent.getWorldQuaternion(parentQ);
    parentWorldQuats.push(parentQ);
  }

  // Add the tip position (end of the last bone — use its first child bone's position,
  // or extrapolate from the last two if no child)
  const lastBone = bones.get(chainBoneNames[chainBoneNames.length - 1]);
  if (lastBone) {
    let tipPos: THREE.Vector3 | null = null;
    for (const child of lastBone.children) {
      if ((child as any).isBone) {
        tipPos = new THREE.Vector3();
        child.getWorldPosition(tipPos);
        break;
      }
    }
    if (!tipPos) {
      // Extrapolate: use the last bone's direction
      const lastPos = positions[positions.length - 1];
      if (positions.length >= 2) {
        const prev = positions[positions.length - 2];
        const dir = _v1.subVectors(lastPos, prev);
        tipPos = lastPos.clone().add(dir.multiplyScalar(0.3));
      } else {
        tipPos = lastPos.clone().add(new THREE.Vector3(0, -0.1, 0));
      }
    }
    positions.push(tipPos);
  }

  // Compute bone lengths
  for (let i = 0; i < positions.length - 1; i++) {
    lengths.push(positions[i].distanceTo(positions[i + 1]));
  }

  return {
    positions,
    lengths,
    boneNames: chainBoneNames,
    startLocalQuats,
    parentWorldQuats,
  };
}
